import { defineStore } from 'pinia';
import { Category } from '~/models/Category.models';
import type {
  OrderReceiptModel,
  OrderReceiptSubTotalsModel,
} from '~/models/order/OrderReceipt.api.model';
import {
  createBundledSubscriptionSummaryItem,
  createDiscountCampaignSummaryItem,
  createHardWareSummaryItem,
  createBuyBackSummaryItem,
  createSavingsSummaryItem,
  createStandaloneSubscriptionSummaryItem,
  createStandalonePrePaidSummaryItem,
  createInsuranceItem,
  type SummaryItem,
  createInsuranceLoadingContentRow as createInsuranceLoadingContentRow,
  createAddOnItem,
  createExtraSimItem,
} from '~/models/order/SummaryItem.model';
import type { LabelDto } from '~/models/ListingProduct.api.model';
import { shouldExcludeDiscountFromDisplay } from '~/utils/order/shouldExcludeDiscountFromDisplay';

export const createEmptyOrderCheckoutReceipt = (): OrderReceiptModel => ({
  OrderId: -1,
  OrderGuid: '',
  SubTotals: {} as OrderReceiptSubTotalsModel,
  AppliedDiscounts: [],
  OrderTotalMinimumCost: 0,
  TotalMonthlyAmount: 0,
  Currency: 'SEK',
  EnableBuyback: false,
  HasSentPurchaseEvent: false,
  OrderItems: [],
  PaymentMethodSystemName: '',
  PaymentMethodName: '',
  ShippingMethodSystemName: '',
  ShippingMethodName: '',
  OrderStatusDescription: '',
  OrderStatus: '',
  OrderDate: '',
  DiscountCouponCodes: [],
  DisplaySwishPaymentInstruction: false,
  ConfirmationRequired: false,
  PaymentStatus: '',
  StoreName: '',
});

export type AfterSalesOffers = {
  AfterSalesOffers: AfterSalesOfferItem[];
};
export type AfterSalesOfferItem = {
  Category: string;
  LevelName: string;
  MonthlyPrice: number;
  OfferProductName: string;
  OrderItemId: number;
  ProductVariantName: string;
  Description?: string;
  Label?: LabelDto;
};

type OrderStoreState = {
  ssnThatWasSearched?: string;
  order: OrderReceiptModel;
  afterSalesOffers: AfterSalesOffers;
  insuranceItemWasAdded: boolean;
  buybackItemWasAdded: boolean;
};

export const useMyOrderStore = defineStore('orderStore', {
  state: (): OrderStoreState => ({
    ssnThatWasSearched: '',
    order: createEmptyOrderCheckoutReceipt(),
    afterSalesOffers: { AfterSalesOffers: [] },
    insuranceItemWasAdded: false,
    buybackItemWasAdded: false,
  }),
  actions: {
    setOrder(orderResponse?: OrderReceiptModel): OrderReceiptModel {
      if (!orderResponse || !orderResponse.OrderId) {
        const emptyOrder = createEmptyOrderCheckoutReceipt();
        this.order = emptyOrder;
        return emptyOrder;
      }
      this.order = orderResponse;

      return orderResponse;
    },
    reset() {
      this.$state = {
        ssnThatWasSearched: '',
        order: createEmptyOrderCheckoutReceipt(),
        afterSalesOffers: { AfterSalesOffers: [] },
        insuranceItemWasAdded: false,
        buybackItemWasAdded: false,
      };
    },
  },
  getters: {
    hasFetchedOrder(state: OrderStoreState): boolean {
      return (
        state.order.OrderId !== -1 &&
        Array.isArray(state.order.OrderItems) &&
        state.order.OrderItems.length > 0
      );
    },
    itemsToListInSummary(state: OrderStoreState): SummaryItem[] {
      const newItemsToList: SummaryItem[] = [];

      for (const item of state.order.OrderItems) {
        if (item.Category === Category.phones) {
          newItemsToList.push(createHardWareSummaryItem(item));

          if (item.BundlePlanItem && !!item.BundlePlanItem.Id) {
            newItemsToList.push(
              createBundledSubscriptionSummaryItem(
                item.BundlePlanItem,
                state.order.AppliedDiscounts,
              ),
            );
          }

          if (item.BundlePlanItem?.FamilyPlanChildPlanItems?.length) {
            for (const childPlan of item.BundlePlanItem
              .FamilyPlanChildPlanItems) {
              newItemsToList.push(
                createBundledSubscriptionSummaryItem(
                  childPlan,
                  state.order.AppliedDiscounts,
                  true,
                ),
              );
            }
          }
        }

        if (item.Category === Category.buyback) {
          newItemsToList.push(createBuyBackSummaryItem(item));
        }

        if (item.Category === Category.insurances) {
          newItemsToList.push(createInsuranceItem(item));
        }

        if (item.Category === Category.accessory) {
          newItemsToList.push(createHardWareSummaryItem(item));
        }

        if (
          item.Category === Category.postPaid ||
          item.Category === Category.mobileBroadBand
        ) {
          newItemsToList.push(
            createStandaloneSubscriptionSummaryItem(
              item,
              state.order.AppliedDiscounts,
              item.ProductName.toLocaleLowerCase().includes('familj'),
              item.Category === Category.mobileBroadBand ? true : false,
            ),
          );
        }

        if (item.Category === Category.prePaid) {
          newItemsToList.push(createStandalonePrePaidSummaryItem(item));
        }

        if (item.Category === Category.addOn) {
          newItemsToList.push(createAddOnItem(item));
        }

        if (item.Category === Category.extraSim) {
          newItemsToList.push(createExtraSimItem(item));
        }
      }

      if (
        state.order.SubTotals.SubTotalSavings &&
        state.order.SubTotals.SubTotalSavings > 0
      ) {
        newItemsToList.push(createSavingsSummaryItem(state.order.SubTotals));
      }

      if (
        state.order.AppliedDiscounts &&
        state.order.AppliedDiscounts.length > 0
      ) {
        // Skip discounts with "försäkring" in the name (handled by Category.insurances item)
        // Skip free product campaigns (listed as products with price 0)
        for (const discount of state.order.AppliedDiscounts) {
          if (shouldExcludeDiscountFromDisplay(discount)) {
            continue;
          }

          newItemsToList.push(createDiscountCampaignSummaryItem(discount));
        }
      }

      const noInsuranceItem = !state.order.OrderItems.find(
        (item) => item.Category === Category.insurances,
      );
      const hasJustAddedInsurance = this.insuranceItemWasAdded;
      const shouldShowLoadingRowForInsurance =
        noInsuranceItem && hasJustAddedInsurance;

      if (shouldShowLoadingRowForInsurance) {
        newItemsToList.push(createInsuranceLoadingContentRow());
      }

      let sortedItemsToList =
        order_sortDiscountsBeforeTheSavingsRow(newItemsToList);
      sortedItemsToList =
        order_sortInsuranceLoadingItemRowToWhereInsuranceRowIs(newItemsToList);

      return sortedItemsToList;
    },
    shouldShowMessage(state: OrderStoreState): boolean {
      const message = state.order.OrderStatusMessage?.Message;
      const visibleFrom = state.order.OrderStatusMessage?.VisibleFromUtc;
      const visibleTo = state.order.OrderStatusMessage?.VisibleToUtc;

      if (!message) {
        return false;
      }

      if (!visibleFrom || !visibleTo) {
        return false;
      }

      const visibleFromDate = new Date(visibleFrom);
      const visibleToDate = new Date(visibleTo);
      const currentDate = new Date();

      if (currentDate < visibleFromDate || currentDate >= visibleToDate) {
        return false;
      }

      return true;
    },
    shouldShowTotalDirectPaymentSum(state: OrderStoreState): boolean {
      if (
        state.order.OrderItems.every(
          (item) =>
            item.Category === Category.postPaid || Category.mobileBroadBand,
        )
      ) {
        return false;
      }

      return true;
    },
  },
});
